import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Compass, ShieldCheck, Truck, RotateCcw, FileText, Award, Mail, Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import { getProduct, getProducts } from '../api/client';
import ProductCard from '../components/ProductCard';
import SellerPublicProfilePage from './SellerPublicProfilePage';

const DynamicPage = ({ storeSettings }) => {
    const params = useParams();
    const rawSlug = String(params.slug || params.sellerSlug || params.username || '').trim();
    const isSeller = rawSlug.startsWith('@') || /^seller-/i.test(rawSlug) || /^vendor-/i.test(rawSlug);

    if (isSeller) {
        return <SellerPublicProfilePage />;
    }

    const slug = rawSlug.toLowerCase();

    const page = useMemo(() => {
        const pages = Array.isArray(storeSettings?.pages) ? storeSettings.pages : [];
        return pages.find(item => {
            const pageSlug = String(item?.slug || '').toLowerCase();
            const visible = item?.visible !== false && String(item?.visibility || '').toLowerCase() !== 'hidden';
            return visible && pageSlug === slug;
        }) || null;
    }, [slug, storeSettings?.pages]);

    const activeLanguage = useMemo(() => {
        try {
            const geoContextStr = localStorage.getItem('luxvono_geo_context');
            const geoContext = geoContextStr ? JSON.parse(geoContextStr) : null;
            return (geoContext && geoContext.language) ? geoContext.language : 'ar';
        } catch (_) {
            return 'ar';
        }
    }, [page]);

    const isAr = activeLanguage === 'ar';

    // Premium Localized Luxury Policy Content
    const getLuxuryPolicyContent = (pageSlug, isArabic) => {
        const matchingSlug = (list) => list.includes(pageSlug);

        // 1. About Us
        if (matchingSlug(['about-us', 'about', 'our-story', 'من-نحن'])) {
            return {
                title: isArabic ? "عالم لوكسفونو وتراثنا الفاخر" : "The World of Luxvono & Our Heritage",
                subtitle: isArabic ? "إرث من الحرفية، التميز الحصري، والاقتناء الفاخر" : "A legacy of craftsmanship, curated excellence, and luxury sourcing",
                coverImage: "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?auto=format&fit=crop&w=1200&q=80",
                cards: isArabic ? [
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "قصتنا وفلسفتنا",
                        desc: "تأسست لوكسفونو لتقديم معايير غير مسبوقة في عالم التسوق الفاخر. نؤمن بأن كل اقتناء يمثل فصلاً من فصول الذوق الرفيع والتعبير الشخصي الفريد."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "الاستيراد والاقتناء الخاص",
                        desc: "نوفر لعملائنا وصولاً استثنائياً لأندر الساعات السويسرية، المجوهرات الراقية، والحقائب الجلدية المخصصة عبر شبكة علاقاتنا العالمية الحصرية."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "أصالة مطلقة وضمان تام",
                        desc: "تخضع كل قطعة في مجموعتنا لفحص دقيق ومعمق من قبل خبرائنا المعتمدين لضمان أصالتها الكاملة وجودتها الاستثنائية بلا مساومة."
                    }
                ] : [
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "Our Philosophy",
                        desc: "Luxvono was established to redefine the benchmarks of luxury commerce. We believe that every acquisition is a chapter in the art of refined personal expression."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "Bespoke Sourcing",
                        desc: "We provide our clients with exclusive access to the rarest Swiss timepieces, fine jewelry, and bespoke leather goods through our global concierge network."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "Absolute Authenticity",
                        desc: "Every item in our collection is rigorously inspected and authenticated by our certified master specialists to guarantee absolute authenticity."
                    }
                ]
            };
        }

        // 2. Shipping & Delivery
        if (matchingSlug(['shipping', 'shipping-policy', 'delivery', 'الشحن'])) {
            return {
                title: isArabic ? "شحن وتوصيل كونسيرج الفاخر" : "Complimentary Global Concierge Delivery",
                subtitle: isArabic ? "خدمات لوجستية مؤمنة، متتبعة بالكامل، ومغلفة بلمسة من الرقي" : "Secured, insured, and tracked premium white-glove logistics",
                coverImage: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
                cards: isArabic ? [
                    {
                        icon: <Truck size={24} style={{ color: '#C5A880' }} />,
                        title: "نقل عالمي مؤمن وسريع",
                        desc: "نشحن عبر أرقى خطوط النقل العالمية (DHL Express, FedEx) بتأمين شامل يضمن سلامة قطعكم الثمينة وتتبعاً فورياً لحظة بلحظة."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "تغليف لوكسفونو الفاخر",
                        desc: "تصلكم مشترياتكم مغلفة في صناديقنا الحصرية المصممة للحفاظ على رونق القطع، والمغلقة بأختام أمنية خاصة لا يمكن المساس بها."
                    },
                    {
                        icon: <Clock size={24} style={{ color: '#C5A880' }} />,
                        title: "مواعيد التوصيل المعتمدة",
                        desc: "يستغرق الشحن السريع كونسيرج ما بين 3 إلى 5 أيام عمل للمدن الكبرى في دول الخليج العربي وجميع الوجهات العالمية الكبرى."
                    }
                ] : [
                    {
                        icon: <Truck size={24} style={{ color: '#C5A880' }} />,
                        title: "Insured Global Transit",
                        desc: "We deliver via premier courier services (DHL Express, FedEx) with full insurance coverage, ensuring your acquisition is fully protected and tracked."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "Signature Gift Packaging",
                        desc: "Your acquisitions arrive in our signature luxury presentation box, protected by high-security security seals to ensure untouched authenticity."
                    },
                    {
                        icon: <Clock size={24} style={{ color: '#C5A880' }} />,
                        title: "Delivery Timelines",
                        desc: "Premium white-glove transit averages 3-5 business days to the GCC region and all major metropolitan capitals worldwide."
                    }
                ]
            };
        }

        // 3. Returns & Refunds
        if (matchingSlug(['returns', 'returns-policy', 'returns-refunds', 'refunds', 'الاسترجاع'])) {
            return {
                title: isArabic ? "سياسة الاسترجاع السلس والتبديل" : "The Seamless Return Privilege",
                subtitle: isArabic ? "مهلة مرنة وضمان كامل لراحة بالكم ورضاكم المطلق" : "A hassle-free and complimentary commitment to your complete satisfaction",
                coverImage: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
                cards: isArabic ? [
                    {
                        icon: <RotateCcw size={24} style={{ color: '#C5A880' }} />,
                        title: "مهلة 14 يوماً للاسترجاع",
                        desc: "نوفر لكم مهلة 14 يوماً من تاريخ الاستلام لطلب استرجاع أو استبدال المشتريات، شريطة أن تكون القطع بحالتها الأصلية وغير مستخدمة."
                    },
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "استلام مجاني من موقعك",
                        desc: "يقوم فريق الكونسيرج بترتيب موعد لاستلام المرتجعات مجاناً من باب منزلك عبر مندوبنا الخاص دون أي عناء أو تكاليف إضافية."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "استرداد سريع وآمن",
                        desc: "بعد فحص القطعة والتحقق من حالتها، يتم معالجة طلب استرداد الأموال فوراً وإعادتها لبطاقتكم الائتمانية أو وسيلة الدفع المستخدمة."
                    }
                ] : [
                    {
                        icon: <RotateCcw size={24} style={{ color: '#C5A880' }} />,
                        title: "14-Day Grace Period",
                        desc: "We offer a flexible 14-day return window from the date of delivery to request a return or exchange for any eligible, unworn pieces."
                    },
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "Complimentary Collection",
                        desc: "Our concierge team will coordinate a complimentary courier collection directly from your residence at your preferred time."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "Swift Processing",
                        desc: "Upon inspection and verification of the returned item in its original condition, your refund is instantly credited back to your original payment method."
                    }
                ]
            };
        }

        // 4. Privacy Policy
        if (matchingSlug(['privacy-policy', 'privacy', 'الخصوصية'])) {
            return {
                title: isArabic ? "سياسة الخصوصية وحماية البيانات" : "Client Privacy & Data Discretion",
                subtitle: isArabic ? "التزامنا التام بالحفاظ على سرية بياناتكم وحمايتها بأعلى معايير الأمان" : "Our absolute commitment to your complete confidentiality and data protection",
                coverImage: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80",
                cards: isArabic ? [
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "حماية البيانات المشفرة",
                        desc: "نطبق بروتوكولات تشفير عسكرية متطورة لحماية بياناتكم الشخصية ومعلومات تصفحكم ومنع أي وصول غير مصرح به نهائياً."
                    },
                    {
                        icon: <FileText size={24} style={{ color: '#C5A880' }} />,
                        title: "سرية المعاملات والمدفوعات",
                        desc: "تتم معالجة المدفوعات عبر بوابات دفع ممتثلة لمعايير PCI-DSS الأمنية العالمية لضمان سرية وحماية تفاصيل بطاقاتكم."
                    },
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "عدم مشاركة البيانات",
                        desc: "نلتزم بشكل مطلق بعدم بيع أو مشاركة أو تأجير بياناتكم الشخصية لأي جهات خارجية لأغراض تسويقية دون موافقة صريحة منكم."
                    }
                ] : [
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "Military-Grade Encryption",
                        desc: "We utilize advanced encryption protocols to safeguard your personal data and browsing information, ensuring complete confidentiality."
                    },
                    {
                        icon: <FileText size={24} style={{ color: '#C5A880' }} />,
                        title: "Secure Payment Gateways",
                        desc: "All financial transactions are routed through ultra-secure, certified PCI-DSS compliant gateways to protect your credit details."
                    },
                    {
                        icon: <Compass size={24} style={{ color: '#C5A880' }} />,
                        title: "Zero Data Distribution",
                        desc: "We strictly guarantee that your personal information is never sold, shared, or leased to third parties for commercial gain."
                    }
                ]
            };
        }

        // 5. Terms of Service
        if (matchingSlug(['terms', 'terms-of-service', 'terms-and-conditions', 'الشروط'])) {
            return {
                title: isArabic ? "الشروط والأحكام وعلاقة العملاء" : "Terms & Conditions of Client Relations",
                subtitle: isArabic ? "الإطار القانوني والتنظيمي لتقديم خدماتنا الفاخرة وضمان حقوقكم" : "The legal and regulatory framework governing our bespoke services and your rights",
                coverImage: "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=1200&q=80",
                cards: isArabic ? [
                    {
                        icon: <FileText size={24} style={{ color: '#C5A880' }} />,
                        title: "عقد الخدمات الفاخرة",
                        desc: "تخضع جميع المبيعات والخدمات المقدمة عبر لوكسفونو لشروط الاستخدام الموثقة هنا لضمان تقديم خدمة تليق بتوقعاتكم العالية."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "حقوق الملكية الفكرية",
                        desc: "جميع المواد والشعارات والتصاميم والصور المعروضة على المنصة هي ملكية فكرية حصرية للوكسفونو ويمنع إعادة استخدامها تجارياً."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "القانون الحاكم والنزاعات",
                        desc: "تخضع هذه الشروط والأحكام وتفسر وفقاً للقوانين والأنظمة المعمول بها والموثقة لدى الإدارة المحلية المنظمة للخدمة."
                    }
                ] : [
                    {
                        icon: <FileText size={24} style={{ color: '#C5A880' }} />,
                        title: "Bespoke Services Contract",
                        desc: "All purchases, acquisitions, and concierge services are regulated under the service protocols documented here to ensure seamless standards."
                    },
                    {
                        icon: <Award size={24} style={{ color: '#C5A880' }} />,
                        title: "Intellectual Property Rights",
                        desc: "All design systems, logos, product photography, and texts displayed on this platform are the exclusive intellectual property of Luxvono."
                    },
                    {
                        icon: <ShieldCheck size={24} style={{ color: '#C5A880' }} />,
                        title: "Governing Jurisdictions",
                        desc: "These terms and agreements are formulated and governed in accordance with local and international corporate law frameworks."
                    }
                ]
            };
        }

        return null;
    };

    const [linkedProducts, setLinkedProducts] = useState([]);
    const [linkedProduct, setLinkedProduct] = useState(null);
    const [linkedLoading, setLinkedLoading] = useState(false);

    useEffect(() => {
        if (!page) {
            setLinkedProducts([]);
            setLinkedProduct(null);
            return;
        }

        const fetchLinkedData = async () => {
            if (page.linkType === 'category' && page.linkId) {
                try {
                    setLinkedLoading(true);
                    const res = await getProducts(0, 12, '', page.linkId);
                    const list = Array.isArray(res) ? res : res?.data || res?.content || [];
                    setLinkedProducts(list);
                } catch (err) {
                    console.error('Failed to load linked category products:', err);
                } finally {
                    setLinkedLoading(false);
                }
            } else if (page.linkType === 'product' && page.linkId) {
                try {
                    setLinkedLoading(true);
                    const res = await getProduct(page.linkId);
                    const data = res?.data || res;
                    setLinkedProduct(data);
                } catch (err) {
                    console.error('Failed to load linked product:', err);
                } finally {
                    setLinkedLoading(false);
                }
            } else {
                setLinkedProducts([]);
                setLinkedProduct(null);
            }
        };

        fetchLinkedData();
    }, [page]);

    const renderLinkedSection = () => {
        if (linkedLoading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid rgba(197, 168, 128, 0.1)',
                        borderTop: '3px solid #C5A880',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            );
        }

        if (page?.linkType === 'category' && linkedProducts.length > 0) {
            return (
                <div className="linked-category-products" style={{ marginTop: '50px' }}>
                    <h2 style={{
                        fontFamily: isAr ? "'Cairo', sans-serif" : 'inherit',
                        fontSize: '1.5rem',
                        fontWeight: '750',
                        color: 'var(--text-primary)',
                        marginBottom: '24px',
                        borderBottom: '1px solid var(--border-color, #eaeaea)',
                        paddingBottom: '12px'
                    }}>
                        {isAr ? 'المنتجات المميزة في هذا القسم' : 'Featured Products in this Category'}
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: '24px'
                    }}>
                        {linkedProducts.map(prod => (
                            <ProductCard key={prod.id} product={prod} />
                        ))}
                    </div>
                </div>
            );
        }

        if (page?.linkType === 'product' && linkedProduct) {
            return (
                <div className="linked-product-featured" style={{ marginTop: '50px' }}>
                    <h2 style={{
                        fontFamily: isAr ? "'Cairo', sans-serif" : 'inherit',
                        fontSize: '1.5rem',
                        fontWeight: '750',
                        color: 'var(--text-primary)',
                        marginBottom: '24px',
                        borderBottom: '1px solid var(--border-color, #eaeaea)',
                        paddingBottom: '12px'
                    }}>
                        {isAr ? 'المنتج الموصى به' : 'Recommended Product'}
                    </h2>
                    <div style={{
                        display: 'flex',
                        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                        gap: '30px',
                        background: 'var(--bg-card, #ffffff)',
                        border: '1px solid var(--border-color, #eaeaea)',
                        borderRadius: '24px',
                        padding: '30px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.02)',
                        alignItems: 'center'
                    }}>
                        <div style={{ flex: '1', width: '100%', height: '240px', borderRadius: '16px', overflow: 'hidden' }}>
                            <img 
                                src={linkedProduct.primaryImageUrl || (linkedProduct.images && linkedProduct.images[0]) || '/placeholder.png'} 
                                alt={isAr ? (linkedProduct.nameAr || linkedProduct.name) : (linkedProduct.nameEn || linkedProduct.name)} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        </div>
                        <div style={{ flex: '1.5', width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <span style={{
                                textTransform: 'uppercase',
                                fontSize: '12px',
                                fontWeight: '700',
                                color: '#C5A880',
                                letterSpacing: '1px'
                            }}>
                                {isAr ? 'عرض خاص وحصري' : 'Exclusive Feature'}
                            </span>
                            <h3 style={{
                                fontFamily: isAr ? "'Cairo', sans-serif" : 'inherit',
                                fontSize: '1.4rem',
                                fontWeight: '800',
                                color: 'var(--text-primary)',
                                margin: 0
                            }}>
                                {isAr ? (linkedProduct.nameAr || linkedProduct.name) : (linkedProduct.nameEn || linkedProduct.name)}
                            </h3>
                            <p style={{
                                fontSize: '14px',
                                color: 'var(--text-secondary, #666)',
                                lineHeight: '1.7',
                                margin: 0
                            }}>
                                {isAr ? (linkedProduct.shortDescriptionAr || linkedProduct.descriptionAr || linkedProduct.description) : (linkedProduct.shortDescriptionEn || linkedProduct.descriptionEn || linkedProduct.description)}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px' }}>
                                <span style={{
                                    fontSize: '1.6rem',
                                    fontWeight: '800',
                                    color: '#C5A880'
                                }}>
                                    {linkedProduct.price ? `$${linkedProduct.price.toLocaleString()}` : ''}
                                </span>
                            </div>
                            <Link 
                                to={`/product/${linkedProduct.id}`} 
                                className="lux-policy-footer-btn"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    padding: '12px 24px',
                                    background: '#111111',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '30px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                    marginTop: '5px',
                                    alignSelf: 'flex-start'
                                }}
                            >
                                <span>{isAr ? 'عرض التفاصيل واقتناء القطعة' : 'View Details & Acquire'}</span>
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    const luxuryPolicy = getLuxuryPolicyContent(slug, isAr);

    if (!page && !luxuryPolicy) {
        return (
            <div className="container dynamic-page dynamic-page--empty" dir={isAr ? 'rtl' : 'ltr'}>
                <h1 style={{ fontFamily: isAr ? "'Cairo', sans-serif" : 'inherit' }}>{isAr ? 'الصفحة غير موجودة' : 'Page Not Found'}</h1>
                <p>{isAr ? 'هذه الصفحة غير متوفرة حالياً أو تم إخفاؤها من قبل الإدارة.' : 'This page is not available or currently hidden by admin settings.'}</p>
                <Link to="/" className="btn-primary">{isAr ? 'العودة للرئيسية' : 'Back Home'}</Link>
            </div>
        );
    }

    // Render Luxury Design if matching policy slugs, else fallback to default backend content
    if (luxuryPolicy) {
        return (
            <article className="container dynamic-page lux-policy-container" dir={isAr ? 'rtl' : 'ltr'} style={{
                maxWidth: '1000px',
                margin: '0 auto',
                padding: '20px 20px 80px 20px',
                textAlign: isAr ? 'right' : 'left'
            }}>
                <style>{`
                    .lux-policy-header {
                        text-align: center;
                        margin-bottom: 50px;
                    }
                    .lux-policy-title {
                        font-size: 2.4rem;
                        font-weight: 800;
                        color: var(--text-primary, #111);
                        margin-bottom: 14px;
                        letter-spacing: -0.5px;
                        line-height: 1.25;
                    }
                    .lux-policy-subtitle {
                        font-size: 1.15rem;
                        color: var(--text-secondary, #666);
                        max-width: 600px;
                        margin: 0 auto;
                        line-height: 1.6;
                    }
                    .lux-policy-cover-wrap {
                        width: 100%;
                        height: 350px;
                        border-radius: 24px;
                        overflow: hidden;
                        margin-bottom: 60px;
                        box-shadow: 0 16px 40px rgba(0,0,0,0.04);
                        border: 1px solid rgba(0,0,0,0.05);
                    }
                    body.theme-dark .lux-policy-cover-wrap {
                        border-color: rgba(255,255,255,0.05);
                    }
                    .lux-policy-cover {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                    .lux-policy-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                        gap: 30px;
                        margin-bottom: 60px;
                    }
                    .lux-policy-card {
                        background: var(--bg-card, #ffffff);
                        border: 1px solid var(--border-color, #eaeaea);
                        border-radius: 20px;
                        padding: 30px;
                        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                        box-shadow: 0 4px 20px rgba(0,0,0,0.01);
                    }
                    .lux-policy-card:hover {
                        transform: translateY(-4px);
                        border-color: rgba(197, 168, 128, 0.5);
                        box-shadow: 0 12px 30px rgba(197, 168, 128, 0.08);
                    }
                    .lux-policy-card-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;
                        background: rgba(197, 168, 128, 0.1);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 20px;
                    }
                    .lux-policy-card-title {
                        font-size: 1.15rem;
                        font-weight: 700;
                        color: var(--text-primary, #111);
                        margin-bottom: 12px;
                    }
                    .lux-policy-card-desc {
                        font-size: 0.95rem;
                        color: var(--text-secondary, #666);
                        line-height: 1.7;
                    }
                    .lux-policy-footer {
                        text-align: center;
                        border-top: 1px solid var(--border-color, #eaeaea);
                        padding-top: 40px;
                    }
                    .lux-policy-footer-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 14px 30px;
                        background: #111111;
                        color: #ffffff;
                        border: none;
                        border-radius: 30px;
                        font-size: 14px;
                        font-weight: 700;
                        text-decoration: none;
                        transition: all 0.2s ease;
                        box-shadow: 0 8px 24px rgba(0,0,0,0.1);
                    }
                    body.theme-dark .lux-policy-footer-btn {
                        background: #ffffff;
                        color: #111111;
                    }
                    .lux-policy-footer-btn:hover {
                        transform: scale(1.03);
                        box-shadow: 0 12px 32px rgba(197, 168, 128, 0.25);
                    }
                    
                    /* RTL Adjustments */
                    [dir="rtl"] .lux-policy-title,
                    [dir="rtl"] .lux-policy-card-title {
                        font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                    }
                    [dir="rtl"] .lux-policy-subtitle,
                    [dir="rtl"] .lux-policy-card-desc {
                        font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                        font-weight: 400;
                    }
                    [dir="rtl"] .lux-policy-footer-btn {
                        font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                        flex-direction: row-reverse;
                    }
                `}</style>

                <div className="lux-policy-header">
                    <h1 className="lux-policy-title">{luxuryPolicy.title}</h1>
                    <p className="lux-policy-subtitle">{luxuryPolicy.subtitle}</p>
                </div>

                {luxuryPolicy.coverImage && (
                    <div className="lux-policy-cover-wrap">
                        <img src={luxuryPolicy.coverImage} alt={luxuryPolicy.title} className="lux-policy-cover" />
                    </div>
                )}

                <div className="lux-policy-grid">
                    {luxuryPolicy.cards.map((card, index) => (
                        <div key={index} className="lux-policy-card">
                            <div className="lux-policy-card-icon">{card.icon}</div>
                            <h3 className="lux-policy-card-title">{card.title}</h3>
                            <p className="lux-policy-card-desc">{card.desc}</p>
                        </div>
                    ))}
                </div>

                <div className="lux-policy-footer">
                    <Link to="/contact" className="lux-policy-footer-btn">
                        <span>{isAr ? "تواصل مع كونسيرج لوكسفونو" : "Contact Luxvono Concierge"}</span>
                        <ArrowRight size={16} />
                    </Link>
                </div>
                {renderLinkedSection()}
            </article>
        );
    }

    // Standard fallback to backend content if not matching standard policy pages
    const pageTitle = isAr ? (page.title_ar || page.title) : (page.title_en || page.title);
    const pageContent = isAr ? (page.content_ar || page.content) : (page.content_en || page.content);

    return (
        <article className="container dynamic-page" dir={isAr ? 'rtl' : 'ltr'} style={{ textAlign: isAr ? 'right' : 'left', padding: '40px 20px 80px 20px' }}>
            {page.imageUrl && (
                <img src={page.imageUrl} alt={pageTitle} className="dynamic-page-cover" loading="lazy" style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '16px', marginBottom: '40px' }} />
            )}
            <h1 className="dynamic-page-title" style={{ fontFamily: isAr ? "'Noto Sans Arabic', 'Cairo', sans-serif" : 'inherit', fontSize: '2rem', fontWeight: '800', marginBottom: '24px' }}>
                {pageTitle}
            </h1>
            <div 
                className="dynamic-page-content" 
                style={{ 
                    fontFamily: isAr ? "'Noto Sans Arabic', 'Cairo', sans-serif" : 'inherit',
                    lineHeight: 1.8,
                    fontSize: '16px',
                    color: 'var(--text-secondary)'
                }}
                dangerouslySetInnerHTML={{ __html: pageContent || '' }}
            />
            {renderLinkedSection()}
        </article>
    );
};

export default DynamicPage;
